import { useEffect, useRef, useState } from "react";
import api, { fmtDateTime, formatApiError, eur } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { amdenNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConciergeBell, Upload, FileText, Settings2, Loader2, Pencil, Trash2 } from "lucide-react";
import { logoSrc } from "@/components/BrandLogo";
import { toast } from "sonner";

const STATUS = { pending: "bg-amber-50 text-amber-700", quoted: "bg-sky-50 text-sky-700", accepted: "bg-emerald-50 text-emerald-700", declined: "bg-rose-50 text-rose-700", cancelled: "bg-zinc-100 text-zinc-500" };

export default function ServiceRequests() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const [quoteFor, setQuoteFor] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const load = () => api.get("/service-requests").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const filtered = rows.filter((r) => filter === "all" || r.status === filter);

  return (
    <AdminLayout nav={amdenNav} title="Service Requests">
      <PageHeader title="Client Service Requests" subtitle="Review requests from clients and send quotes">
        <Button data-testid="manage-catalog-btn" variant="outline" onClick={() => setCatalogOpen(true)}><Settings2 className="h-4 w-4 mr-1" />Manage catalog</Button>
      </PageHeader>

      <div className="flex gap-2 mb-4">
        {["all", "pending", "quoted", "accepted", "declined"].map((s) => (
          <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)} className={`text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full ${filter === s ? "bg-slate-900 text-white" : "bg-zinc-100 text-zinc-500"}`}>{s}</button>
        ))}
      </div>

      <div className="space-y-3">
        {filtered.length === 0 && <div className="text-zinc-400 text-sm">No requests.</div>}
        {filtered.map((r) => (
          <div key={r.id} data-testid={`request-${r.id}`} className="bg-white border border-zinc-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold text-slate-900">{r.service_name}</span>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded ${STATUS[r.status]}`}>{r.status}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{r.company_name} · requested by {r.requested_by} · {fmtDateTime(r.created_at)}</div>
                {r.notes && <p className="text-sm text-zinc-600 mt-2">{r.notes}</p>}
                {r.attachment_url && <a href={logoSrc(r.attachment_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-600 mt-2"><FileText className="h-3.5 w-3.5" />Client attachment</a>}
                {r.quote && (
                  <div className="mt-3 bg-sky-50 border border-sky-100 rounded-md p-3 text-sm">
                    <div className="font-bold text-sky-800">Quote sent: {eur(r.quote.amount)}</div>
                    {r.quote.notes && <div className="text-zinc-600 mt-1">{r.quote.notes}</div>}
                    {r.quote.file_url && <a href={logoSrc(r.quote.file_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-sky-600 mt-1"><FileText className="h-3.5 w-3.5" />Quote PDF</a>}
                    {r.quote.link && <a href={r.quote.link} target="_blank" rel="noreferrer" className="block text-xs text-sky-600 mt-1 truncate">{r.quote.link}</a>}
                  </div>
                )}
              </div>
              {r.status === "pending" && <Button size="sm" className="bg-amber-500 text-black font-bold shrink-0" data-testid={`quote-btn-${r.id}`} onClick={() => setQuoteFor(r)}>Send quote</Button>}
            </div>
          </div>
        ))}
      </div>

      {quoteFor && <QuoteDialog req={quoteFor} onClose={() => setQuoteFor(null)} onDone={() => { setQuoteFor(null); load(); }} />}
      {catalogOpen && <CatalogDialog onClose={() => setCatalogOpen(false)} />}
    </AdminLayout>
  );
}

function QuoteDialog({ req, onClose, onDone }) {
  const ref = useRef(null);
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [link, setLink] = useState("");
  const [fileUrl, setFileUrl] = useState(null);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const uploadFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try { const fd = new FormData(); fd.append("file", file); const res = await api.post("/service-requests/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setFileUrl(res.data.file_url); setFileName(res.data.filename); toast.success("File uploaded"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const send = async () => {
    try { await api.post(`/service-requests/${req.id}/quote`, { amount, notes, link, file_url: fileUrl }); toast.success("Quote sent"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Quote: {req.service_name}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Amount (€)</Label><Input data-testid="quote-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1" /></div>
          <div><Label>Notes</Label><Textarea data-testid="quote-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1" /></div>
          <div><Label>Link (optional)</Label><Input data-testid="quote-link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" className="mt-1" /></div>
          <div>
            <Label>PDF (optional)</Label>
            <input ref={ref} type="file" accept=".pdf,image/*" className="hidden" data-testid="quote-file-input" onChange={(e) => uploadFile(e.target.files?.[0])} />
            <Button variant="outline" size="sm" className="mt-1" data-testid="quote-upload" disabled={busy} onClick={() => ref.current?.click()}>{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1" />{fileName || "Upload PDF"}</>}</Button>
          </div>
          <Button data-testid="quote-send" onClick={send} className="w-full bg-slate-900 text-white">Send quote</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CatalogDialog({ onClose }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: "", description: "" });
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/service-catalog").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Service name required"); return; }
    try {
      if (editing) { await api.put(`/service-catalog/${editing}`, { name: form.name, description: form.description }); toast.success("Service updated"); }
      else { await api.post("/service-catalog", form); toast.success("Service added"); }
      setForm({ name: "", description: "" }); setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggle = async (s) => { await api.put(`/service-catalog/${s.id}`, { active: !s.active }); load(); };
  const startEdit = (s) => { setEditing(s.id); setForm({ name: s.name, description: s.description || "" }); };
  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? Clients will no longer see it.`)) return;
    try { await api.delete(`/service-catalog/${s.id}`); toast.success("Service deleted"); if (editing === s.id) { setEditing(null); setForm({ name: "", description: "" }); } load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Service catalog</DialogTitle></DialogHeader>
        <p className="text-xs text-zinc-500 -mt-1">Toggle to hide a service from clients, or edit / delete it. Changes apply to every client's request page.</p>
        <div className="max-h-72 overflow-y-auto divide-y divide-zinc-50 border border-zinc-200 rounded-md mt-2">
          {rows.length === 0 && <div className="px-3 py-4 text-sm text-zinc-400">No services yet.</div>}
          {rows.map((s) => (
            <div key={s.id} data-testid={`catalog-${s.id}`} className="flex items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0"><div className={`text-sm ${s.active ? "font-medium text-zinc-800" : "text-zinc-400 line-through"}`}>{s.name}</div><div className="text-xs text-zinc-400 truncate">{s.description}</div></div>
              <div className="flex items-center gap-1.5 shrink-0">
                <Switch data-testid={`catalog-toggle-${s.id}`} checked={!!s.active} onCheckedChange={() => toggle(s)} />
                <Button data-testid={`catalog-edit-${s.id}`} size="sm" variant="outline" className="h-8 w-8 p-0" onClick={() => startEdit(s)}><Pencil className="h-3.5 w-3.5" /></Button>
                <Button data-testid={`catalog-delete-${s.id}`} size="sm" variant="outline" className="h-8 w-8 p-0 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => remove(s)}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-2 mt-1">
          <div className="text-xs font-semibold uppercase tracking-wider text-zinc-500">{editing ? "Edit service" : "Add a service"}</div>
          <Input data-testid="cat-name" placeholder="Service name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input data-testid="cat-desc" placeholder="Short description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <div className="flex gap-2">
            <Button data-testid="cat-add" onClick={save} className="flex-1 bg-slate-900 text-white">{editing ? "Save changes" : "Add service"}</Button>
            {editing && <Button variant="outline" data-testid="cat-cancel" onClick={() => { setEditing(null); setForm({ name: "", description: "" }); }}>Cancel</Button>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
